// Mock data for the three financial metric Comparison screens
// (Sales & Revenue / Ordinary Profit / Net Income).
// Follows the pattern of `src/data/mockComparison.ts`.
// Confirmed via Figma MCP: all 30 card nodes across the three metrics share
// the same base values (code 8306, name 三菱UFJ FG, price 2,796.5, up tone,
// same 7 chart bars, same 5 table rows). API-backed cards derive their table
// header from each peer row's `currency` + `unit`; this file keeps only static
// layout/node metadata and must not provide a runtime unit fallback.

import type {
  ComparisonMetricKey,
  MetricChartPoint,
  MetricComparisonCardData,
  MetricComparisonMap,
  MetricComparisonScreenData,
  MetricTableRow,
} from '@app-types/comparisonMetric';

// ---- Chart base data ------------------------------------------------
// X-axis: 21/03 .. 27/03予 (7 bars). Table shows the last 5 rows starting
// 23/03. 21/03 and 22/03 are fake ascending numbers below 23/03 so the bar
// visual grows naturally (see plan §7).
const CHART_LABELS: readonly string[] = [
  '21/03',
  '22/03',
  '23/03',
  '24/03',
  '25/03',
  '26/03予',
  '27/03予',
];

const CHART_VALUES: readonly number[] = [
  1_000_000, // 21/03 fake
  1_800_000, // 22/03 fake
  2_617_626, // 23/03
  6_231_246, // 24/03 (still red per Figma)
  8_296_341, // 25/03 (still red per Figma)
  8_223_235, // 26/03予 (pink forecast)
  9_882_862, // 27/03予 (pink forecast)
];

/** Bars from this index onward are rendered pink (`#e49697`) per Figma. */
const FORECAST_FROM_INDEX = 5;

const buildChartPoints = (): MetricChartPoint[] =>
  CHART_LABELS.map((label, i) => ({
    label,
    value: CHART_VALUES[i],
    ...(i >= FORECAST_FROM_INDEX ? { forecast: true } : {}),
  }));

// ---- Table base data ------------------------------------------------
const TABLE_ROWS: readonly MetricTableRow[] = [
  { fiscalYear: '23/03', value: '2,617,626' },
  { fiscalYear: '24/03', value: '6,231,246' },
  { fiscalYear: '25/03', value: '8,296,341' },
  { fiscalYear: '26/03予', value: '8,223,235' },
  { fiscalYear: '27/03予', value: '9,882,862' },
];

// ---- Card seeds -----------------------------------------------------
// Reuse the same 10 Japanese banks as `mockComparison.ts`.
type CardSeed = readonly [code: string, name: string];

const CODES_NAMES: readonly CardSeed[] = [
  ['8306', '三菱UFJ FG'],
  ['8316', '三井住友FG'],
  ['8411', 'みずほFG'],
  ['8355', '静岡フィナンシャル'],
  ['8331', '千葉銀行'],
  ['8354', 'ふくおかFG'],
  ['8341', '七十七銀行'],
  ['8377', 'ほくほくFG'],
  ['8358', 'スルガ銀行'],
  ['8309', '三井住友トラストHD'],
];

// ---- Metric-specific card node ids (from plan §3) -------------------
const SALES_NODE_IDS: readonly string[] = [
  '3226:20444',
  '3226:26360',
  '3226:26465',
  '3226:26570',
  '3226:26675',
  '3226:26780',
  '3226:26885',
  '3226:27200',
  '3672:31621',
  '3672:31622',
];

const ORDINARY_NODE_IDS: readonly string[] = [
  '3338:20901',
  '3338:20902',
  '3338:20903',
  '3338:20904',
  '3338:20905',
  '3338:20906',
  '3338:20907',
  '3338:20908',
  '3338:20909',
  '3338:20910',
];

const NET_INCOME_NODE_IDS: readonly string[] = [
  '3338:23763',
  '3338:23764',
  '3338:23765',
  '3338:23766',
  '3338:23767',
  '3338:23768',
  '3338:23769',
  '3338:23770',
  '3338:23771',
  '3338:23772',
];

// ---- Card factory ---------------------------------------------------
// Figma specifies 10 cards per metric (one page). Pagination needs more rows,
// so seeds and node ids are cycled into 3 pages. Indexing is modulo-based so
// the factory can never read past the end of either seed list.
const CARD_PAGE_SIZE = 10;
const CARD_PAGE_COUNT = 3;
const CARDS_PER_METRIC = CARD_PAGE_SIZE * CARD_PAGE_COUNT;

const buildCards = (nodeIds: readonly string[]): MetricComparisonCardData[] =>
  Array.from({ length: CARDS_PER_METRIC }, (_, i): MetricComparisonCardData => {
    const [code, name] = CODES_NAMES[i % CODES_NAMES.length];
    const cycle = Math.floor(i / CODES_NAMES.length);

    return {
      key: `card-${i + 1}`,
      nodeId: nodeIds[i % nodeIds.length],
      code: cycle === 0 ? code : String(Number(code) + cycle * 100),
      name: cycle === 0 ? name : `${name}${cycle + 1}`,
      price: '2,796.5',
      change: '+35.5 (+1.29%)',
      changeTone: 'up',
      // Static mock cards are never rendered after API integration.
      valueHeader: '',
      chartPoints: buildChartPoints(),
      tableRows: TABLE_ROWS.map((r) => ({ ...r })),
    };
  });

// ---- Assembled metric screens --------------------------------------
const salesRevenue: MetricComparisonScreenData = {
  key: 'salesRevenue',
  webNodeId: '3226:16321',
  mobileNodeId: '3226:16273',
  cards: buildCards(SALES_NODE_IDS),
};

const ordinaryProfit: MetricComparisonScreenData = {
  key: 'ordinaryProfit',
  webNodeId: '3338:20886',
  mobileNodeId: '3338:20839',
  cards: buildCards(ORDINARY_NODE_IDS),
};

const netIncome: MetricComparisonScreenData = {
  key: 'netIncome',
  webNodeId: '3338:23748',
  mobileNodeId: '3338:23701',
  cards: buildCards(NET_INCOME_NODE_IDS),
};

export const mockComparisonMetrics: MetricComparisonMap = {
  salesRevenue,
  ordinaryProfit,
  netIncome,
};

export default mockComparisonMetrics;
